import { ErrorFallback } from "@/components/ErrorFallback";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Text } from "@/components/podkit/typography/Text";
import { NewSecretModal } from "@/components/secrets/NewSecretModal";
import { SecretsCard } from "@/components/secrets/SecretsCard";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useListOrganizationSecrets, type PlainSecret } from "@/queries/secret-queries";
import { useOrganization } from "@/queries/organization-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { OrganizationSecretsEducationPage } from "@/routes/settings/organization/secrets/OrganizationSecretsEducationPage";
import type { PlainMessage } from "@bufbuild/protobuf";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { SecretScope } from "gitpod-next-api/gitpod/v1/secret_pb";
import { useCallback, useContext, useEffect, useMemo, useState, type FC } from "react";
import { SettingsPageContext } from "@/contexts/SettingsPageContext";
import { withGitpodSize } from "@/assets/icons/utils";
import IconPlusSmall from "central-icons/IconPlusSmall";
import { Button } from "@/components/flexkit/Button";

export const OrganizationSecretsPage: FC = () => {
    useDocumentTitle("Organization Secrets");

    const { data: organization, isPending: isPendingOrganization } = useOrganization();
    const { data: user, isPending: isUserPending, error: userError } = useAuthenticatedUser();
    const { data: secrets, isPending, error } = useListOrganizationSecrets(user?.organizationId);
    const { setBreadCrumbRowAction } = useContext(SettingsPageContext);
    const [showAddSecretModal, setShowAddSecretModal] = useState(false);

    const isEnterpriseTier = organization?.tier === OrganizationTier.ENTERPRISE;

    const onNewSecret = useCallback(() => {
        setShowAddSecretModal(true);
    }, [setShowAddSecretModal]);

    const content = useMemo(() => {
        if (error || userError) {
            return <ErrorFallback error={error || userError!} />;
        }

        if (isPending || !user) {
            return (
                <SkeletonBlock
                    ready={!isPending && !isUserPending}
                    failed={!!error || !!userError}
                    className="h-[160px] w-[800px]"
                />
            );
        }

        const scope = new SecretScope({ scope: { case: "organizationId", value: user?.organizationId } });
        return <OrganizationSecrets scope={scope} secrets={secrets?.secrets ?? []} onNewSecret={onNewSecret} />;
    }, [error, userError, isPending, isUserPending, user, secrets, onNewSecret]);

    useEffect(() => {
        if (!isEnterpriseTier || (secrets?.secrets ?? []).length === 0) {
            return; // in this case we're showing the education page
        }

        setBreadCrumbRowAction(
            <Button
                size="sm"
                LeadingIcon={withGitpodSize(IconPlusSmall)}
                onClick={onNewSecret}
                data-testid="add-secret-modal-trigger"
                data-tracking-id="new-secret-organization-secrets-page"
            >
                New Secret
            </Button>,
        );

        return () => {
            setBreadCrumbRowAction(null);
        };
    }, [isEnterpriseTier, onNewSecret, secrets?.secrets, setBreadCrumbRowAction]);

    if (isPendingOrganization) {
        return <LoadingState />;
    }

    if (!isEnterpriseTier) {
        return <OrganizationSecretsEducationPage />;
    }

    return (
        <div data-testid="organization-secrets-page">
            {content}
            <NewSecretModal
                open={showAddSecretModal}
                enableSecretContainerRegistry={true}
                scope={new SecretScope({ scope: { case: "organizationId", value: user!.organizationId } })}
                onClose={() => setShowAddSecretModal(false)}
            />
        </div>
    );
};

const OrganizationSecrets: FC<{
    scope: PlainMessage<SecretScope>;
    secrets: PlainSecret[];
    onNewSecret: () => void;
}> = ({ scope, secrets, onNewSecret }) => {
    return (
        <div className="flex flex-col gap-6">
            <Text className="text-base text-content-secondary">
                These are organization-wide secrets that are available in all environments created within this
                organization.
            </Text>
            <SecretsCard scope={scope} secrets={secrets} showCreatorColumn={true} onNewSecret={onNewSecret} />
        </div>
    );
};
